// Per-spool breakdown grouped: page → spool (sectionId) → items.
// Driven by item.pageNumber + item.sectionId. Items missing either
// fall under "(no page)" / "(no spool)" buckets so nothing
// disappears silently. Sub-totals per spool: pipe metres + labour
// (cuts, roll grooves, welds) computed via shared/labour so they
// match the Consolidated Summary + Excel export.
import type { TakeoffItemDto } from "../../../../../shared/types/pipe-takeoff";
import { computeSpoolLabour } from "../../../../../shared/labour";

interface Props {
  items: TakeoffItemDto[];
}

interface Group<T> {
  key: string;
  label: string;
  rows: T[];
}

function groupItems(items: TakeoffItemDto[]): Group<Group<TakeoffItemDto>>[] {
  const byPage = new Map<string, Map<string, TakeoffItemDto[]>>();
  for (const it of items) {
    const pageKey = it.pageNumber !== undefined ? String(it.pageNumber) : "_none";
    const spoolKey = it.sectionId ?? "_none";
    if (!byPage.has(pageKey)) byPage.set(pageKey, new Map());
    const spools = byPage.get(pageKey)!;
    if (!spools.has(spoolKey)) spools.set(spoolKey, []);
    spools.get(spoolKey)!.push(it);
  }
  const sortedPages = Array.from(byPage.entries()).sort(([a], [b]) => {
    if (a === "_none") return 1;
    if (b === "_none") return -1;
    return Number(a) - Number(b);
  });
  return sortedPages.map(([pageKey, spools]) => ({
    key: pageKey,
    label: pageKey === "_none" ? "(no page)" : `Page ${pageKey}`,
    rows: Array.from(spools.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([spoolKey, rows]) => ({
        key: spoolKey,
        label: spoolKey === "_none" ? "(no spool)" : spoolKey,
        rows,
      })),
  }));
}

function spoolStats(rows: TakeoffItemDto[]) {
  const labour = computeSpoolLabour(rows);
  const counts: Record<string, number> = {};
  for (const r of rows) counts[r.category] = (counts[r.category] ?? 0) + r.quantity;
  return { labour, counts };
}

export function PerSpoolTab({ items }: Props) {
  const groups = groupItems(items);
  if (groups.length === 0) {
    return (
      <p style={{ color: "var(--bsp-fg-muted)" }}>
        No items to break down per spool yet.
      </p>
    );
  }
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {groups.map((page) => (
        <div key={page.key} className="bsp-card" style={{ padding: 0 }}>
          <div style={{ padding: "12px 16px", background: "var(--bsp-blue)", color: "var(--ink-000)" }}>
            <strong>{page.label}</strong>{" "}
            <span style={{ opacity: 0.85, fontSize: 12 }}>
              · {page.rows.length} spool{page.rows.length === 1 ? "" : "s"}
            </span>
          </div>
          {page.rows.map((spool) => {
            const stats = spoolStats(spool.rows);
            return (
              <div key={spool.key} style={{ borderTop: "1px solid var(--bsp-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 16px", background: "var(--bsp-blue-pale)" }}>
                  <strong className="bsp-mono">{spool.label}</strong>
                  <span style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}>
                    {Object.entries(stats.counts)
                      .map(([cat, n]) => `${cat} ${n}`)
                      .join(" · ")}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 16, padding: "6px 16px", fontSize: 12, color: "var(--bsp-fg-muted)", background: "var(--bsp-bg-subtle)" }}>
                  <span>pipe <strong className="bsp-mono">{stats.labour.pipeMetres.toFixed(2)} m</strong></span>
                  <span>cuts <strong className="bsp-mono">{stats.labour.cuts}</strong></span>
                  <span>roll grooves <strong className="bsp-mono">{stats.labour.rollGrooves}</strong></span>
                  <span>welds <strong className="bsp-mono">{stats.labour.welds}</strong></span>
                </div>
                <table className="bsp-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Size</th>
                      <th>Material</th>
                      <th>Qty</th>
                      <th>Length (m)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spool.rows.map((it) => (
                      <tr key={it.id}>
                        <td>{it.category}</td>
                        <td>{it.description}</td>
                        <td className="bsp-mono">{it.sizeDn ?? "—"}</td>
                        <td>{it.material ?? "—"}</td>
                        <td>{it.quantity}</td>
                        <td className="bsp-mono">
                          {it.lengthM !== undefined ? it.lengthM.toFixed(2) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
